import { useCallback } from 'react';

import { useVideoPlatformCallback, useVisitorVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { AppDependencies } from '../../state';
import { useAppDependenciesContext } from '../../state/useAppDependenciesContext';
import { Stopwatch } from '../../utils';
import { VideoPlatform, normalizeVideoPlatform } from '../../video-sdk';
import { useCoerceVideoPlatform as useBaseCoerceVideoPlatform } from '../../video-sdk/actions/useLifecycleActions';
import {
  VideoPlatformCoercionStrategy,
  useVideoPlatformCoercionStratCallback as useBaseVideoPlatformCoercionStratCallback,
} from './useVideoPlatformCoercionStratCallback';

export type UseGracefullyCoerceProviderVideoPlatformOptions = {
  /**
   * Enabled dependency injection. Defaults to {@link useBaseCoerceVideoPlatform}.
   */
  useCoerceVideoPlatform?: typeof useBaseCoerceVideoPlatform;
  /**
   * Enables dependency injection. Defaults to {@link useBaseVideoPlatformCoercionStratCallback}.
   */
  useVideoPlatformCoercionStratCallback?: typeof useBaseVideoPlatformCoercionStratCallback;
};

function showErrorToast(toast: AppDependencies['toast']) {
  toast.error('Unable to admit due to an unexpected error. Please refresh if this issue persists.');
}

function useAutomaticProviderVideoPlatformCoercionCallback({
  useCoerceVideoPlatform: useCoerceVideoPlatform = useBaseCoerceVideoPlatform,
}: Pick<Required<UseGracefullyCoerceProviderVideoPlatformOptions>, 'useCoerceVideoPlatform'>) {
  const { logger, toast } = useAppDependenciesContext();
  const coerce = useCoerceVideoPlatform();
  return useCallback(
    async ({
      visitorVideoPlatform,
      providerVideoPlatform,
    }: {
      visitorVideoPlatform: VideoPlatform | null;
      providerVideoPlatform: VideoPlatform | null;
    }) => {
      const targetPlatform = normalizeVideoPlatform(visitorVideoPlatform);
      const logContext = {
        fromPlatform: providerVideoPlatform,
        targetPlatform,
      };

      logger.info('Coercing provider video platform', logContext);
      const stopwatch = Stopwatch.start();
      try {
        await coerce(targetPlatform);
        logger.info('Successfully coerced provider video platform', {
          ...logContext,
          durationMs: stopwatch.stop(),
        });
        return true;
      } catch (error) {
        logger.error(
          'Failed to coerce provider video platform',
          {
            ...logContext,
            durationMs: stopwatch.stop(),
          },
          error instanceof Error ? error : undefined,
        );
        showErrorToast(toast);
        return false;
      }
    },
    [coerce, logger, toast],
  );
}

/**
 * Wraps {@link useCoerceVideoPlatform} with graceful error handling around admission.
 */
export function useGracefullyCoerceProviderVideoPlatformCallback({
  useCoerceVideoPlatform = useBaseCoerceVideoPlatform,
  useVideoPlatformCoercionStratCallback = useBaseVideoPlatformCoercionStratCallback,
}: UseGracefullyCoerceProviderVideoPlatformOptions = {}) {
  const identifyVideoPlatformCoercionStrat = useVideoPlatformCoercionStratCallback();
  const handleVideoPlatformCoercion = useAutomaticProviderVideoPlatformCoercionCallback({
    useCoerceVideoPlatform,
  });
  const getProviderVideoPlatform = useVideoPlatformCallback();
  const getVisitorVideoPlatform = useVisitorVideoPlatformCallback();
  const { logger, toast } = useAppDependenciesContext();

  return useCallback(
    async (visitorId: string): Promise<boolean> => {
      const coercionStrategy = identifyVideoPlatformCoercionStrat(visitorId);
      const visitorVideoPlatform = getVisitorVideoPlatform(visitorId);
      const providerVideoPlatform = getProviderVideoPlatform();

      switch (coercionStrategy) {
        case VideoPlatformCoercionStrategy.Auto:
          return await handleVideoPlatformCoercion({ visitorVideoPlatform, providerVideoPlatform });
        case VideoPlatformCoercionStrategy.Manual:
          // The UI should prevent the provider from admitting and hitting this
          // scenario upstream, but this is a safeguard in case of unexpected
          // states.
          logger.error("Cannot coerce provider's video platform under current conditions", {
            visitorId,
            visitorVideoPlatform,
            providerVideoPlatform,
          });
          showErrorToast(toast);
          return false;
        case VideoPlatformCoercionStrategy.Delegate:
          logger.info('Delegating video platform coercion to client user', {
            providerVideoPlatform,
            visitorVideoPlatform,
          });
          return true;
        case VideoPlatformCoercionStrategy.NoOp:
        default:
          return true;
      }
    },
    [
      getProviderVideoPlatform,
      getVisitorVideoPlatform,
      handleVideoPlatformCoercion,
      identifyVideoPlatformCoercionStrat,
      logger,
      toast,
    ],
  );
}
